import React from "react";
import { Document, Image, Link, Page, Text, View } from "@react-pdf/renderer";
import { registerPdfFonts } from "../../../utils";
import { AI_DISCLAIMER_BODY, PRIVACY_LABEL, PRIVACY_URL, TERMS_LABEL, TERMS_URL } from "../../../config/aiDisclaimer";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
    ANON_PRICE_TEASER,
    RESULTS_STATS,
    composePackageBlurb,
    composeWhyThisMix,
    contentTypeDuration,
    tierShortBlurb,
} from "../strategyCopy";
import { StrategyData, StrategyUnit, unitCount } from "../strategy/StrategyData";
import { ProposalTier, TIER_ORDER, TierEstimate } from "../types";
import { channelBlurb, DECK } from "./deckCopy";
import { GOLD_SOFT, HINT, MUTED, styles } from "./pdfStyles";

registerPdfFonts();

// Category-specific use-case videos + expert bench. Images arrive as data
// URLs (prefetched in downloadProposalPdf) so @react-pdf never fetches
// cross-origin; entries whose image failed to load carry null and render
// without one.
export interface PdfUseCaseVideo {
    title: string;
    contentType: string | null;
    brandLine: string | null;
    duration: string | null;
    thumbnailDataUrl: string | null;
}

export interface PdfExpert {
    name: string;
    credential: string | null;
    blurb: string | null;
    industryYears: string | null;
    avatarDataUrl: string | null;
    // Server-composed Expert IQ relevance line (affinity band, years, top
    // product match) — the "why this expert" copy.
    relevance: string | null;
}

export interface ProposalPdfInput {
    strategy: StrategyData;
    proposal: {
        id: number | null;
        name: string | null;
        brandName: string | null;
        contactEmail: string | null;
        createdAt: string | null;
        shareUrl: string | null;
        pricingTotal: number | string | null;
        // The PDP audit this proposal was built from, if any.
        auditUrl?: string | null;
        auditDate?: string | null;
    };
    // Per-tier estimates (from /public/proposals/preview) for the
    // three-column Investment page; totals fall back to "Custom quote".
    tiers?: Partial<Record<ProposalTier, TierEstimate>> | null;
    // Per-tier unit lists so non-selected tiers can list their mix.
    unitsByTier?: Partial<Record<ProposalTier, StrategyUnit[]>> | null;
    useCases?: PdfUseCaseVideo[] | null;
    experts?: PdfExpert[] | null;
    internal?: boolean;
    // Dollar figures render only when this is not explicitly false
    // (TDC-1563): anonymous renders swap the Investment amounts for the
    // ANON_PRICE_TEASER line. Defaults to priced so existing internal/RMN
    // callers are unaffected.
    showPricing?: boolean;
    // TDC-1591: the ANON_PRICE_TEASER contains a dollar figure ("$20k") —
    // RMN renders pass false to suppress it too, leaving an unpriced PDF
    // with no teaser. Defaults true so anonymous renders are unaffected.
    showPriceTeaser?: boolean;
    // RMN white-label portal: swaps retailer-specific channel copy (see
    // RMN_CHANNEL_BLURBS) into the "Where It Lives" table.
    rmn?: boolean;
}

const TIER_LABELS: Record<ProposalTier, string> = {
    good: "Good",
    better: "Better",
    best: "Best",
};

const fmtDate = (iso: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

function Footer({ input, page }: { input: ProposalPdfInput; page: number }) {
    return (
        <View style={styles.footerNote} fixed>
            <Text>
                {DECK.company}
                {input.proposal.brandName ? ` × ${input.proposal.brandName}` : ""}
            </Text>
            <Text>
                {input.proposal.id ? `Strategy #${input.proposal.id} · ` : ""}
                {page}
            </Text>
        </View>
    );
}

function SectionHeader({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
    return (
        <View>
            <Text style={styles.eyebrow}>{eyebrow}</Text>
            <View style={styles.headRule} />
            <Text style={styles.sectionTitle}>{title}</Text>
            {sub ? <Text style={styles.sub}>{sub}</Text> : null}
        </View>
    );
}

function StatTiles({
    stats,
}: {
    stats: Array<{ stat: string; label: string; source?: string }>;
}) {
    return (
        <View style={styles.statRow}>
            {stats.map((s) => (
                <View key={s.label} style={styles.statTile}>
                    <Text style={styles.statValue}>{s.stat}</Text>
                    <Text style={styles.statLabel}>{s.label}</Text>
                    {s.source ? <Text style={styles.statSource}>{s.source}</Text> : null}
                </View>
            ))}
        </View>
    );
}

function MixRow({
    unit,
    index,
    optionalLabel,
    price,
}: {
    unit: StrategyUnit;
    index: number | null;
    optionalLabel?: string | null;
    price?: string | null;
}) {
    const duration = contentTypeDuration(unit.name);
    return (
        <View style={styles.mixRow} wrap={false}>
            <Text style={[styles.mixBadge, ...(optionalLabel ? [styles.mixBadgeOptional] : [])]}>
                {optionalLabel ? "+" : index}
            </Text>
            <View style={{ flex: 1 }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Text style={styles.mixTitle}>
                        {unit.quantity > 1 ? `${unit.quantity}× ` : ""}
                        {unit.displayName}
                    </Text>
                    {price ? <Text style={styles.mixPrice}>{price}</Text> : null}
                </View>
                <Text style={styles.mixMeta}>
                    {[duration, optionalLabel].filter(Boolean).join("  ·  ")}
                </Text>
                {unit.reason ? <Text style={styles.mixReason}>{unit.reason}</Text> : null}
            </View>
        </View>
    );
}

export function ProposalPdf({ input }: { input: ProposalPdfInput }) {
    const { strategy, proposal, tiers, unitsByTier, useCases, experts, internal, rmn } = input;
    // Priced unless the caller explicitly opted out (anonymous renders).
    const priced = input.showPricing !== false;
    const hasShowcase =
        (useCases && useCases.length > 0) || (experts && experts.length > 0);
    const whyMix = composeWhyThisMix(strategy.objectiveSlug, strategy.secondaryObjectiveSlug);
    const packageBlurb =
        strategy.objectiveName && strategy.objectiveSlug
            ? composePackageBlurb(
                strategy.tier,
                { name: strategy.objectiveName, slug: strategy.objectiveSlug },
                strategy.secondaryObjectiveName && strategy.secondaryObjectiveSlug
                    ? {
                        name: strategy.secondaryObjectiveName,
                        slug: strategy.secondaryObjectiveSlug,
                    }
                    : null,
            )
            : null;

    const selectedEstimate = tiers ? tiers[strategy.tier] : undefined;
    const perContentType = new Map(
        ((internal && priced && selectedEstimate && selectedEstimate.per_content_type_estimates) || []).map(
            (e) => [e.content_type_id, e],
        ),
    );

    const tierOrderSelectedFirst: ProposalTier[] = [
        strategy.tier,
        ...TIER_ORDER.filter((t) => t !== strategy.tier),
    ];

    let pageNum = 1;
    const nextPage = () => {
        pageNum += 1;
        return pageNum;
    };

    return (
        <Document
            title={proposal.name || "Video Strategy"}
            author={DECK.company}
        >
            {/* 1 — Cover */}
            <Page size="A4" style={styles.blackPage}>
                <Text style={styles.coverCompany}>
                    {proposal.brandName ? `${proposal.brandName.toUpperCase()}  ×  ` : ""}
                    {DECK.company}
                </Text>
                <Text style={styles.coverTitle}>{DECK.docTitle}</Text>
                <View style={styles.coverRule} />
                <Text style={styles.coverMeta}>
                    {proposal.name || "Expert-led video strategy"}
                </Text>
                <Text style={styles.coverMeta}>
                    {[
                        proposal.brandName ? `Prepared for ${proposal.brandName}` : null,
                        fmtDate(proposal.createdAt),
                        proposal.id ? `Strategy #${proposal.id}` : null,
                    ]
                        .filter(Boolean)
                        .join("  ·  ")}
                </Text>
            </Page>

            {/* 2 — Who we are / trust at scale */}
            <Page size="A4" style={styles.page}>
                <SectionHeader eyebrow={DECK.whoWeAre.eyebrow} title={DECK.whoWeAre.headline} />
                <Text style={styles.paragraph}>{DECK.whoWeAre.body}</Text>
                <Text style={[styles.paragraph, { color: MUTED }]}>{DECK.whoWeAre.scene}</Text>
                <Text style={[styles.eyebrow, { marginTop: 14 }]}>TRUST AT SCALE</Text>
                <StatTiles stats={DECK.trustStats} />
                <Text style={[styles.eyebrow, { marginTop: 16 }]}>BRAND PARTNERS INCLUDE</Text>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
                    {DECK.brandPartners.map((partner) => (
                        <View
                            key={partner}
                            style={{
                                width: "31%",
                                borderWidth: 0.5,
                                borderColor: "#E5E1DB",
                                borderRadius: 6,
                                paddingVertical: 14,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Text
                                style={{
                                    fontFamily: "Oswald-Bold",
                                    fontSize: 13,
                                    color: "#1C1A17",
                                    textTransform: "uppercase",
                                    letterSpacing: 1.5,
                                }}
                            >
                                {partner}
                            </Text>
                        </View>
                    ))}
                </View>
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 3 — The proof */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow={DECK.proof.eyebrow}
                    title={DECK.proof.headline}
                    sub={DECK.proof.sub}
                />
                <StatTiles stats={DECK.proof.stats} />
                <Text style={[styles.eyebrow, { marginTop: 16 }]}>{DECK.retailProof.eyebrow}</Text>
                <Text style={[styles.sub, { marginBottom: 4 }]}>{DECK.retailProof.headline}</Text>
                <StatTiles stats={DECK.retailProof.stats} />
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 4 — Your strategy (dynamic) */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow="YOUR STRATEGY"
                    title="HERE'S YOUR RECOMMENDED VIDEO STRATEGY"
                    sub="Based on your goals and placements, this custom video mix is designed to deliver the biggest impact."
                />
                {proposal.auditUrl ? (
                    <View style={[styles.darkCallout, { marginTop: 0, marginBottom: 12, padding: 10 }]}>
                        <Text style={[styles.darkCalloutBody, { fontSize: 8.5 }]}>
                            Grounded in your PDP audit
                            {proposal.auditDate ? ` (${fmtDate(proposal.auditDate)})` : ""}: this
                            strategy targets the conversion gaps our 14-dimension audit surfaced on{" "}
                            {proposal.auditUrl}
                        </Text>
                    </View>
                ) : null}
                <View style={{ flexDirection: "row", gap: 12 }}>
                    <View style={{ width: 190 }}>
                        <View style={styles.kvBlock}>
                            <Text style={styles.kvLabel}>Primary goal</Text>
                            <Text style={styles.kvValue}>{strategy.objectiveName || "—"}</Text>
                            {strategy.secondaryObjectiveName ? (
                                <>
                                    <Text style={styles.kvLabel}>Secondary goal</Text>
                                    <Text style={styles.kvValue}>{strategy.secondaryObjectiveName}</Text>
                                </>
                            ) : null}
                            {strategy.categoryName ? (
                                <>
                                    <Text style={styles.kvLabel}>Category</Text>
                                    <Text style={styles.kvValue}>{strategy.categoryName}</Text>
                                </>
                            ) : null}
                            <Text style={styles.kvLabel}>Package</Text>
                            <Text style={styles.kvValue}>{TIER_LABELS[strategy.tier]}</Text>
                            <Text style={styles.kvLabel}>Where it lives</Text>
                            {strategy.channels.map((ch) => (
                                <Text key={ch.id} style={{ fontSize: 9.5, marginBottom: 1 }}>
                                    {ch.name}
                                    {ch.quantity > 1 ? `  ×${ch.quantity}` : ""}
                                </Text>
                            ))}
                            <Text style={{ fontSize: 9.5, color: MUTED, marginTop: 2 }}>
                                Agentic AI — included
                            </Text>
                        </View>
                    </View>
                    <View style={{ flex: 1 }}>
                        {packageBlurb ? (
                            <Text style={styles.paragraph}>{packageBlurb}</Text>
                        ) : null}
                        <Text style={[styles.eyebrow, { marginTop: 8 }]}>WHY THIS MIX WORKS</Text>
                        <Text style={{ fontFamily: "Lato-Bold", fontSize: 11, marginBottom: 6 }}>
                            {whyMix.headline}
                        </Text>
                        {whyMix.bullets.map((b, i) => (
                            <View key={i} style={styles.bullet}>
                                <Text style={[styles.bulletArrow, { color: "#A58450" }]}>✓</Text>
                                <Text style={[styles.bulletText, { fontSize: 9.5, color: "#1C1A17" }]}>{b}</Text>
                            </View>
                        ))}
                        <Text style={[styles.paragraph, { color: MUTED, fontStyle: "italic" }]}>
                            {whyMix.sellerTalkTrack}
                        </Text>
                    </View>
                </View>
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 5 — Video mix (dynamic) */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow="THE MIX"
                    title="YOUR RECOMMENDED VIDEO MIX"
                    sub="Each package's video mix. Your selected package is shown first."
                />
                {tierOrderSelectedFirst.map((t) => {
                    const units =
                        (unitsByTier && unitsByTier[t]) ||
                        (t === strategy.tier ? strategy.units : []);
                    const est = tiers ? tiers[t] : undefined;
                    const selected = t === strategy.tier;
                    return (
                        <View key={t} style={{ marginBottom: 12 }}>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginTop: 10,
                                    marginBottom: 4,
                                }}
                            >
                                <Text style={[styles.eyebrow, { marginBottom: 0 }]}>
                                    {TIER_LABELS[t]} PACKAGE
                                </Text>
                                {selected ? (
                                    <Text style={[styles.selectedBadge, { marginLeft: 8 }]}>
                                        Your selection
                                    </Text>
                                ) : null}
                                {priced && est && est.total != null ? (
                                    <Text
                                        style={{
                                            marginLeft: "auto",
                                            fontFamily: "Lato-Bold",
                                            fontSize: 11,
                                            color: "#1C1A17",
                                        }}
                                    >
                                        {formatCurrency(est.total)}
                                    </Text>
                                ) : null}
                            </View>
                            {units.map((unit, i) => {
                                const priceEst = selected
                                    ? perContentType.get(unit.contentTypeId)
                                    : undefined;
                                return (
                                    <MixRow
                                        key={unit.contentTypeId}
                                        unit={unit}
                                        index={i + 1}
                                        price={
                                            priceEst
                                                ? priceEst.quantity > 1
                                                    ? `${priceEst.quantity} × ${formatCurrency(priceEst.unit_total)}`
                                                    : formatCurrency(priceEst.unit_total)
                                                : null
                                        }
                                    />
                                );
                            })}
                        </View>
                    );
                })}
                <Text style={[styles.eyebrow, { marginTop: 10 }]}>HOW THIS DRIVES RESULTS</Text>
                <StatTiles
                    stats={RESULTS_STATS.map((s) => ({
                        stat: s.stat,
                        label: `${s.title} — ${s.description}`,
                        source: s.source,
                    }))}
                />
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 6 — Where it lives (dynamic) */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow="WHERE IT LIVES"
                    title="ONE EXPERT SHOOT, EVERY CHANNEL YOU SELL"
                    sub="Every channel cut from the same expert footage. One shoot, many surfaces."
                />
                <View style={styles.tableHead}>
                    <Text style={[styles.tableHeadCell, { width: 120 }]}>Channel</Text>
                    <Text style={[styles.tableHeadCell, { width: 80 }]}>Placements</Text>
                    <Text style={[styles.tableHeadCell, { flex: 1 }]}>How it works for you</Text>
                </View>
                {strategy.channels.map((ch) => (
                    <View key={ch.id} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: 120, fontFamily: "Lato-Bold" }]}>
                            {ch.name}
                        </Text>
                        <Text style={[styles.tableCell, { width: 80 }]}>{ch.quantity}</Text>
                        <Text style={[styles.tableCell, { flex: 1 }]}>
                            {channelBlurb(ch.name, rmn)}
                        </Text>
                    </View>
                ))}
                <View style={styles.darkCallout}>
                    <Text style={styles.darkCalloutTitle}>{DECK.agentic.title}</Text>
                    <Text style={styles.darkCalloutBody}>{DECK.agentic.body}</Text>
                </View>
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 6b — Use cases + expert bench (dynamic, category-specific) */}
            {hasShowcase ? (
                <Page size="A4" style={styles.page}>
                    {useCases && useCases.length > 0 ? (
                        <View>
                            <SectionHeader
                                eyebrow="USE CASES"
                                title="SEE IT IN ACTION"
                                sub={`Real expert videos produced for ${
                                    strategy.categoryName
                                        ? `${strategy.categoryName.toLowerCase()} `
                                        : ""
                                }products like yours.`}
                            />
                            <View style={{ flexDirection: "row", gap: 10 }}>
                                {useCases.slice(0, 3).map((uc, i) => (
                                    <View key={i} style={[styles.statTile, { flex: 1, padding: 10 }]}>
                                        {uc.thumbnailDataUrl ? (
                                            <Image
                                                src={uc.thumbnailDataUrl}
                                                style={{
                                                    width: "100%",
                                                    height: 84,
                                                    objectFit: "cover",
                                                    borderRadius: 4,
                                                    marginBottom: 6,
                                                }}
                                            />
                                        ) : null}
                                        <Text style={{ fontFamily: "Lato-Bold", fontSize: 9.5 }}>
                                            {uc.contentType || uc.title}
                                        </Text>
                                        {uc.brandLine ? (
                                            <Text style={{ fontSize: 8.5, color: MUTED, marginTop: 2 }}>
                                                {uc.brandLine}
                                            </Text>
                                        ) : null}
                                        {uc.duration ? (
                                            <Text style={{ fontSize: 7.5, color: HINT, marginTop: 2 }}>
                                                {uc.duration}
                                            </Text>
                                        ) : null}
                                    </View>
                                ))}
                            </View>
                        </View>
                    ) : null}

                    {experts && experts.length > 0 ? (
                        <View style={{ marginTop: useCases && useCases.length > 0 ? 18 : 0 }}>
                            <SectionHeader
                                eyebrow="EXPERT SELECTION"
                                title="INDUSTRY EXPERTS FOR YOUR CATEGORY"
                                sub="Matched from 2,300+ credentialed experts by Expert IQ — our per-product scoring of category affinity, credentials, and experience."
                            />
                            <View style={{ flexDirection: "row", gap: 10 }}>
                                {experts.slice(0, 3).map((ex, i) => (
                                    <View key={i} style={[styles.statTile, { flex: 1, padding: 12 }]}>
                                        {ex.avatarDataUrl ? (
                                            <Image
                                                src={ex.avatarDataUrl}
                                                style={{
                                                    width: 52,
                                                    height: 52,
                                                    borderRadius: 26,
                                                    objectFit: "cover",
                                                    marginBottom: 6,
                                                }}
                                            />
                                        ) : null}
                                        <Text style={{ fontFamily: "Lato-Bold", fontSize: 10.5 }}>
                                            {ex.name}
                                        </Text>
                                        {ex.credential ? (
                                            <Text style={{ fontSize: 8.5, color: "#A58450", marginTop: 1 }}>
                                                {ex.credential}
                                                {ex.industryYears
                                                    ? `  ·  ${ex.industryYears} yrs`
                                                    : ""}
                                            </Text>
                                        ) : null}
                                        {ex.relevance ? (
                                            <Text
                                                style={{
                                                    fontSize: 8.5,
                                                    color: MUTED,
                                                    marginTop: 4,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {ex.relevance}
                                            </Text>
                                        ) : ex.blurb ? (
                                            <Text
                                                style={{
                                                    fontSize: 8.5,
                                                    color: MUTED,
                                                    marginTop: 4,
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                {String(ex.blurb).slice(0, 220)}
                                            </Text>
                                        ) : null}
                                    </View>
                                ))}
                            </View>
                            <Text style={[styles.sub, { marginTop: 10, fontSize: 8, color: HINT }]}>
                                Final casting is confirmed during the brief-and-casting step; these experts
                                illustrate the caliber of the bench for your category.
                            </Text>
                        </View>
                    ) : null}
                    <Footer input={input} page={nextPage()} />
                </Page>
            ) : null}

            {/* 7 — Investment (dynamic 3-col) */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow="INVESTMENT"
                    title="ONE STRATEGY. THREE SCOPES."
                    sub={
                        priced
                            ? "Price scales with depth and coverage, not just video count. Your selected package is highlighted."
                            : "Scope scales with depth and coverage, not just video count. Your selected package is highlighted."
                    }
                />
                {!priced && input.showPriceTeaser !== false ? (
                    // The one price slot on an anonymous render (TDC-1563) —
                    // the per-column amounts below are suppressed entirely,
                    // never left to fall back to "Custom quote" ×3. RMN
                    // renders (TDC-1591) suppress this teaser too.
                    <Text
                        style={{
                            fontFamily: "Lato-Bold",
                            fontSize: 12,
                            color: GOLD_SOFT,
                            marginBottom: 10,
                        }}
                    >
                        {ANON_PRICE_TEASER}
                    </Text>
                ) : null}
                <View style={styles.priceCols}>
                    {tierOrderSelectedFirst.map((t) => {
                        const est = tiers ? tiers[t] : undefined;
                        const units = (unitsByTier && unitsByTier[t]) || [];
                        const selected = t === strategy.tier;
                        const videoCount = est ? est.projectCount : unitCount(units);
                        return (
                            <View
                                key={t}
                                style={
                                    selected
                                        ? [styles.priceCol, styles.priceColSelected]
                                        : styles.priceCol
                                }
                            >
                                {selected ? (
                                    <Text style={styles.selectedBadge}>Your selection</Text>
                                ) : null}
                                <Text style={styles.priceColTitle}>{TIER_LABELS[t]}</Text>
                                {priced ? (
                                    <Text style={styles.priceColAmount}>
                                        {est && est.total != null
                                            ? formatCurrency(est.total)
                                            : "Custom quote"}
                                    </Text>
                                ) : null}
                                <View style={styles.priceColLine}>
                                    <Text style={styles.priceColCheck}>✓</Text>
                                    <Text style={styles.priceColLineText}>
                                        {videoCount} expert videos
                                        {priced && est && est.savingsPct > 0
                                            ? ` · save ${Math.round(est.savingsPct)}%`
                                            : ""}
                                    </Text>
                                </View>
                                {units.map((u) => (
                                    <View key={u.contentTypeId} style={styles.priceColLine}>
                                        <Text style={styles.priceColCheck}>✓</Text>
                                        <Text style={styles.priceColLineText}>
                                            {u.quantity > 1 ? `${u.quantity}× ` : ""}
                                            {u.displayName}
                                        </Text>
                                    </View>
                                ))}
                                <View style={styles.priceColLine}>
                                    <Text style={styles.priceColCheck}>✓</Text>
                                    <Text style={styles.priceColLineText}>
                                        {tierShortBlurb(t, !!strategy.secondaryObjectiveName)}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
                <Text style={[styles.sub, { marginTop: 12, fontSize: 8.5, color: HINT }]}>
                    {priced
                        ? DECK.disclaimer
                        : "Final quote confirmed by the TDC team. There is no obligation at this stage."}
                </Text>
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 8 — Process */}
            <Page size="A4" style={styles.page}>
                <SectionHeader
                    eyebrow={DECK.process.eyebrow}
                    title={DECK.process.headline}
                    sub={`Duration: 4–5 weeks. ${DECK.process.tagline}`}
                />
                <View style={styles.processRow}>
                    {DECK.process.steps.slice(0, 3).map((s, i) => (
                        <View key={s.title} style={styles.processStep}>
                            <Text style={styles.processStepNum}>STEP 0{i + 1}</Text>
                            <Text style={styles.processStepTitle}>{s.title}</Text>
                            <Text style={styles.processStepBody}>{s.body}</Text>
                        </View>
                    ))}
                </View>
                <View style={styles.processRow}>
                    {DECK.process.steps.slice(3).map((s, i) => (
                        <View key={s.title} style={styles.processStep}>
                            <Text style={styles.processStepNum}>STEP 0{i + 4}</Text>
                            <Text style={styles.processStepTitle}>{s.title}</Text>
                            <Text style={styles.processStepBody}>{s.body}</Text>
                        </View>
                    ))}
                </View>

                <Text style={[styles.eyebrow, { marginTop: 20 }]}>{DECK.caseStudy.eyebrow}</Text>
                <Text style={[styles.sectionTitle, { fontSize: 13 }]}>{DECK.caseStudy.headline}</Text>
                <Text style={[styles.sub, { marginBottom: 8 }]}>{DECK.caseStudy.brandLine}</Text>
                <View style={{ flexDirection: "row", gap: 10 }}>
                    <View style={[styles.statTile, { flex: 1.4 }]}>
                        <Text style={[styles.kvLabel, { color: "#A58450" }]}>CHALLENGE</Text>
                        <Text style={styles.statLabel}>{DECK.caseStudy.challenge}</Text>
                        <Text style={[styles.kvLabel, { color: "#A58450", marginTop: 8 }]}>SOLUTION</Text>
                        <Text style={styles.statLabel}>{DECK.caseStudy.solution}</Text>
                    </View>
                    <View style={[styles.statTile, { justifyContent: "center" }]}>
                        <Text style={styles.statValue}>{DECK.caseStudy.resultStat}</Text>
                        <Text style={styles.statLabel}>{DECK.caseStudy.resultLabel}</Text>
                        <Text style={styles.statSource}>{DECK.caseStudy.footer}</Text>
                    </View>
                </View>
                <Footer input={input} page={nextPage()} />
            </Page>

            {/* 9 — Next steps (black closing) */}
            <Page size="A4" style={styles.blackPage}>
                <Text style={[styles.eyebrow, { color: GOLD_SOFT }]}>{DECK.nextSteps.eyebrow}</Text>
                <Text style={[styles.coverCompany, { fontSize: 20, lineHeight: 1.3 }]}>
                    {DECK.nextSteps.headline}
                </Text>
                <View style={{ marginTop: 22 }}>
                    {DECK.nextSteps.bullets.map((b, i) => (
                        <View key={i} style={styles.bullet}>
                            <Text style={styles.bulletArrow}>→</Text>
                            <Text style={[styles.bulletText, { color: "#E8E4DD" }]}>{b}</Text>
                        </View>
                    ))}
                </View>
                <View style={{ marginTop: 18, borderTopWidth: 0.5, borderTopColor: "#3F3A33", paddingTop: 14 }}>
                    <Text style={{ fontSize: 10, color: "#B5AFA5", fontStyle: "italic" }}>
                        {DECK.nextSteps.closer}
                    </Text>
                    {proposal.shareUrl ? (
                        <Text style={{ fontSize: 9.5, color: GOLD_SOFT, marginTop: 12 }}>
                            View or edit this strategy any time: {proposal.shareUrl}
                        </Text>
                    ) : null}
                    {proposal.contactEmail ? (
                        <Text style={{ fontSize: 9, color: "#7A7268", marginTop: 4 }}>
                            Prepared for {proposal.contactEmail}
                        </Text>
                    ) : null}
                </View>

                {/* AI / trademark disclaimer — mirrors the PDP audit page's
                    footer, with the tiny TDC mark. */}
                <View
                    style={{
                        position: "absolute",
                        bottom: 26,
                        left: 60,
                        right: 60,
                        flexDirection: "row",
                        gap: 8,
                        alignItems: "flex-start",
                        borderTopWidth: 0.5,
                        borderTopColor: "#3F3A33",
                        paddingTop: 12,
                    }}
                >
                    <Image
                        src={
                            typeof window !== "undefined"
                                ? `${window.location.origin}/logo-transparent-tdc-only-white.png`
                                : "/logo-transparent-tdc-only-white.png"
                        }
                        style={{ width: 14, height: 14, objectFit: "contain", marginTop: 1 }}
                    />
                    <Text style={{ flex: 1, fontSize: 6.5, color: "#7A7268", lineHeight: 1.55 }}>
                        <Text style={{ fontFamily: "Lato-Bold", color: "#9B958C" }}>
                            The Desire Company
                        </Text>
                        {"  —  "}
                        {AI_DISCLAIMER_BODY}
                        <Link src={TERMS_URL} style={{ color: "#9B958C" }}>
                            {TERMS_LABEL}
                        </Link>
                        {" and "}
                        <Link src={PRIVACY_URL} style={{ color: "#9B958C" }}>
                            {PRIVACY_LABEL}
                        </Link>
                        .
                    </Text>
                </View>
            </Page>
        </Document>
    );
}
